/**
 * @file theme.js
 * @description MUI theme — the global design system: color palette, typography,
 *              shape, elevation scale, and component defaults. Professional
 *              blue/indigo identity on a slate neutral base (Inter / Outfit).
 *
 *  Design principles (UI/UX Pro Max):
 *   - One consistent, layered elevation scale (no random shadows).
 *   - Cards rest calmly; hover changes shadow only — never shifts layout.
 *   - Clear type hierarchy with tight heading tracking and readable body.
 *   - Semantic color tokens; data uses tabular figures to avoid jitter.
 */
import { createTheme } from '@mui/material/styles';

// Soft, layered elevation — slate-tinted so shadows feel cohesive on #f8fafc.
const ELEV = {
    sm: '0 1px 2px 0 rgb(15 23 42 / 0.04), 0 1px 3px 0 rgb(15 23 42 / 0.05)',
    md: '0 2px 6px -1px rgb(15 23 42 / 0.06), 0 6px 16px -4px rgb(15 23 42 / 0.08)',
    lg: '0 10px 28px -8px rgb(15 23 42 / 0.14)',
};

const theme = createTheme({
    palette: {
        primary: {
            main: '#2563eb',
            dark: '#1d4ed8',
            light: '#60a5fa',
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#6366f1',
            dark: '#4f46e5',
            light: '#818cf8',
            contrastText: '#ffffff',
        },
        background: {
            default: '#f1f5f9',
            paper: '#ffffff',
        },
        text: {
            primary: '#0f172a',
            secondary: '#64748b',
            disabled: '#94a3b8',
        },
        success: { main: '#10b981', light: '#d1fae5', dark: '#059669', contrastText: '#ffffff' },
        warning: { main: '#f59e0b', light: '#fef3c7', dark: '#b45309', contrastText: '#ffffff' },
        error: { main: '#ef4444', light: '#fee2e2', dark: '#b91c1c', contrastText: '#ffffff' },
        info: { main: '#0ea5e9', light: '#e0f2fe', dark: '#0369a1', contrastText: '#ffffff' },
        divider: 'rgba(148, 163, 184, 0.25)',
        action: {
            hover: 'rgba(37, 99, 235, 0.04)',
            selected: 'rgba(37, 99, 235, 0.08)',
        },
    },
    shape: {
        borderRadius: 14,
    },
    typography: {
        fontFamily: '"Inter", "Outfit", "Roboto", "Helvetica", "Arial", sans-serif',
        // Tight tracking + heavy weight gives headings presence; Outfit for display.
        h1: { fontFamily: '"Outfit", sans-serif', fontWeight: 800, fontSize: '2.25rem', lineHeight: 1.15, letterSpacing: '-0.025em' },
        h2: { fontFamily: '"Outfit", sans-serif', fontWeight: 800, fontSize: '1.875rem', lineHeight: 1.18, letterSpacing: '-0.022em' },
        h3: { fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.2, letterSpacing: '-0.02em' },
        h4: { fontFamily: '"Outfit", sans-serif', fontWeight: 800, fontSize: '1.375rem', lineHeight: 1.2, letterSpacing: '-0.02em' },
        h5: { fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.25, letterSpacing: '-0.015em' },
        h6: { fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1rem', lineHeight: 1.3, letterSpacing: '-0.01em' },
        subtitle1: { fontWeight: 600, lineHeight: 1.5 },
        subtitle2: { fontWeight: 600, lineHeight: 1.45 },
        body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
        body2: { fontSize: '0.8125rem', lineHeight: 1.55 },
        caption: { fontSize: '0.6875rem', lineHeight: 1.4, letterSpacing: '0.01em' },
        overline: { fontWeight: 700, letterSpacing: '0.08em', fontSize: '0.625rem' },
        button: {
            textTransform: 'none',
            fontWeight: 600,
            letterSpacing: 0,
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                // Tabular figures for data-heavy tables/metrics so numbers don't jitter.
                '.tnum, table, .MuiTableCell-root': {
                    fontVariantNumeric: 'tabular-nums',
                },
                '*::-webkit-scrollbar': { width: 10, height: 10 },
                '*::-webkit-scrollbar-thumb': { background: 'rgba(148,163,184,0.5)', borderRadius: 8 },
                '*::-webkit-scrollbar-thumb:hover': { background: 'rgba(100,116,139,0.7)' },
            },
        },
        MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    padding: '8px 18px',
                    boxShadow: 'none',
                    transition: 'background-color .18s ease, box-shadow .18s ease',
                    '&:hover': { boxShadow: '0 4px 12px rgba(37, 99, 235, 0.18)' },
                },
                containedPrimary: {
                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                },
                sizeSmall: { padding: '5px 12px' },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    boxShadow: ELEV.sm,
                    border: '1px solid rgba(148, 163, 184, 0.16)',
                    // Hover changes elevation only — never transform (no layout jitter).
                    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                    backgroundImage: 'none',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: { backgroundImage: 'none' },
                rounded: { borderRadius: 16 },
                elevation1: { boxShadow: ELEV.sm },
                elevation2: { boxShadow: ELEV.md },
                elevation3: { boxShadow: ELEV.md },
                elevation8: { boxShadow: ELEV.lg },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: { fontWeight: 600, borderRadius: 8 },
                sizeSmall: { height: 22, fontSize: '0.6875rem' },
                label: { paddingLeft: 9, paddingRight: 9 },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    '& .MuiTableCell-root': {
                        fontWeight: 700,
                        fontSize: '0.6875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: '#64748b',
                        backgroundColor: '#f8fafc',
                        borderBottom: '1px solid rgba(148, 163, 184, 0.25)',
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottom: '1px solid rgba(148, 163, 184, 0.14)',
                    padding: '12px 16px',
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: { '&:last-child .MuiTableCell-root': { borderBottom: 'none' } },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: '#0f172a',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    borderRadius: 8,
                    padding: '6px 10px',
                },
                arrow: { color: '#0f172a' },
            },
        },
        MuiDialog: {
            styleOverrides: { paper: { borderRadius: 20, boxShadow: ELEV.lg } },
        },
        MuiOutlinedInput: {
            styleOverrides: { root: { borderRadius: 12 } },
        },
    },
});

export default theme;
